import { useState, useEffect, useRef } from 'react';
import { isAuthenticated, getCurrentUser } from './utils/auth';
import { api } from './utils/api';
import { SEED_TASKS, today } from './utils/constants';
import { LoginScreen, UserPickerScreen } from './components/Login';
import { Icons, Spinner } from './components/UI';
import { TasksTab } from './components/Tasks';
import { ShoppingTab } from './components/Shopping';
import { LogTab } from './components/Log';
import { TodayTab } from './components/Today';
import { ReportModal } from './components/Report';
import { BulletinModal, BulletinBoard } from './components/Bulletin';
import { CalendarTab } from './components/Calendar';

const TABS = [
  { id: 'tasks',    label: 'Active',    countKey: 'active' },
  { id: 'archived', label: 'Completed', countKey: 'archived' },
  { id: 'shopping', label: '🛒',        countKey: 'shopping', title: 'Shopping' },
  { id: 'log',      label: '📋',        countKey: null,        title: 'Hours Log' },
  { id: 'today',    label: '☑️',        countKey: null,        title: 'Today' },
  { id: 'calendar', label: '📅',        countKey: null,        title: 'Calendar' },
];

export default function App() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [user, setUser] = useState(getCurrentUser());
  const [store, setStore] = useState(null);
  const [tab, setTab] = useState('tasks');
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showBulletins, setShowBulletins] = useState(false);
  const [bulletinCheck, setBulletinCheck] = useState(false); // true = show modal

  // Refs for tab-specific new actions
  const newActionRef = useRef(null);

  // Load data on login
  useEffect(() => {
    if (!authed || !user) return;
    load();
    // Check bulletins on load
    setBulletinCheck(true);
    // Check bulletins on tab focus
    const onVisible = () => { if (document.visibilityState === 'visible') setBulletinCheck(true); };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(() => { if (document.visibilityState === 'visible') silentRefresh(); }, 300000);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [authed, user]);

  const load = async () => {
    setSyncing(true);
    try {
      const [tasksData, shopData, logData, todayData] = await Promise.all([
        api.getTasks(),
        api.getShopping(),
        api.getLog(),
        api.getToday(user, today()),
      ]);

      let tasks = tasksData.tasks || [];
      let nextId = tasksData.nextId || 200;

      // Seed if empty
      if (tasks.length === 0) {
        await api.seedTasks(SEED_TASKS, 200);
        tasks = SEED_TASKS;
        nextId = 200;
      }

      setStore({
        tasks,
        nextId,
        shopItems: shopData.items || [],
        shopNextId: shopData.nextId || 1,
        logEntries: logData.entries || [],
        logNextId: logData.nextId || 1,
        todayItems: todayData.items || [],
        todayNextId: todayData.nextId || 1,
      });
      setLastSync(new Date());
      setError(null);
    } catch (e) {
      setError('Could not reach the server. Check your connection.');
    }
    setSyncing(false);
  };

  const silentRefresh = async () => {
    try {
      const [tasksData, shopData, logData, todayData] = await Promise.all([
        api.getTasks(),
        api.getShopping(),
        api.getLog(),
        api.getToday(user, today()),
      ]);
      setStore(s => ({
        ...s,
        tasks: tasksData.tasks || [],
        nextId: tasksData.nextId || s.nextId,
        shopItems: shopData.items || [],
        logEntries: logData.entries || [],
        todayItems: todayData.items || [],
      }));
      setLastSync(new Date());
    } catch (e) {}
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  if (!user) return <UserPickerScreen onPick={name => setUser(name)} />;
  if (!store) return error
    ? <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: '#f8fafc' }}>
        <div style={{ color: '#dc2626', fontSize: 15 }}>{error}</div>
        <button onClick={load} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
      </div>
    : <Spinner />;

  const activeTasks = store.tasks.filter(t => t.status !== 'Complete');
  const archivedTasks = store.tasks.filter(t => t.status === 'Complete');
  const unpurchased = (store.shopItems || []).filter(i => !i.purchased);

  const syncLabel = syncing ? '⟳ Syncing...' : lastSync ? `Synced ${lastSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · ${user}` : user;

  const getNewLabel = () => {
    if (tab === 'shopping') return 'Add Item';
    if (tab === 'log') return 'Log Hours';
    if (tab === 'today') return 'Add to Today';
    if (tab === 'calendar') return 'Add Game';
    return 'New';
  };


  const getTabCount = (t) => {
    if (t.id === 'tasks') return activeTasks.length;
    if (t.id === 'archived') return archivedTasks.length;
    if (t.id === 'shopping') return unpurchased.length;
    return null;
  };

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", background: '#f8fafc', minHeight: '100vh', maxWidth: 680, margin: '0 auto' }} onClick={() => showNewMenu && setShowNewMenu(false)}>

      {/* Header */}
      <div style={{ background: '#0f172a', color: '#fff', padding: '10px 12px 0', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 12px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.5, color: '#f1f5f9' }}>SNP Engineering</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{syncLabel}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={load} style={hdrBtn} title="Refresh"><Icons.Refresh /></button>
            <button onClick={() => setShowBulletins(true)} style={hdrBtn} title="Bulletin Board">📢</button>
            <button onClick={() => setShowReport(true)} style={hdrBtn} title="Weekly Report"><Icons.Report /></button>
            {(tab === 'tasks' || tab === 'archived') ? (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden' }}>
                  <button onClick={() => { newActionRef.current?.task?.(); setShowNewMenu(false); }} style={{ background: '#3b82f6', border: 'none', color: '#fff', padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Icons.Plus /> New Task
                  </button>
                  <button onClick={() => setShowNewMenu(m => !m)} style={{ background: '#2563eb', border: 'none', borderLeft: '1px solid #3b82f6', color: '#fff', padding: '8px 10px', cursor: 'pointer', fontSize: 12 }}>▾</button>
                </div>
                {showNewMenu && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.15)', zIndex: 50, minWidth: 160, overflow: 'hidden' }}>
                    <button onClick={() => { newActionRef.current?.task?.(); setShowNewMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, textAlign: 'left', color: '#0f172a', fontWeight: 500 }}>
                      📋 New Task
                    </button>
                    <button onClick={() => { newActionRef.current?.tracker?.(); setShowNewMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, textAlign: 'left', color: '#0f172a', fontWeight: 500, borderTop: '1px solid #f1f5f9' }}>
                      📊 New Trouble Tracker
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => newActionRef.current && (typeof newActionRef.current === 'function' ? newActionRef.current() : newActionRef.current?.task?.())} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icons.Plus /> {getNewLabel()}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 13, padding: '8px 12px', borderRadius: 8, marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
            {error} <span onClick={() => setError(null)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Dismiss</span>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: (tab === 'tasks' || tab === 'archived') ? 0 : 12, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {TABS.map(t => {
            const count = getTabCount(t);
            const isOn = tab === t.id || (tab === 'archived' && t.id === 'archived') || (tab === 'tasks' && t.id === 'tasks');
            const actualOn = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} title={t.title || t.label} style={{ background: actualOn ? '#f8fafc' : 'transparent', border: 'none', color: actualOn ? '#0f172a' : '#64748b', padding: '6px 10px', cursor: 'pointer', fontSize: 13, borderRadius: '8px 8px 0 0', fontWeight: actualOn ? 700 : 500, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t.label}
                {count !== null && <span style={{ background: 'rgba(255,255,255,.15)', padding: '1px 5px', borderRadius: 10, fontSize: 11 }}>{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '0 12px 0' }}>
        {(tab === 'tasks' || tab === 'archived') && (
          <TasksTab
            store={store}
            setStore={setStore}
            currentUser={user}
            defaultView={tab}
            showReport={false}
            setShowReport={setShowReport}
            newActionRef={newActionRef}
          />
        )}
        {tab === 'shopping' && (
          <ShoppingTab
            store={store}
            setStore={setStore}
            currentUser={user}
            newActionRef={newActionRef}
          />
        )}
        {tab === 'log' && (
          <LogTab
            store={store}
            setStore={setStore}
            currentUser={user}
            newActionRef={newActionRef}
          />
        )}
        {tab === 'today' && (
          <TodayTab
            store={store}
            setStore={setStore}
            currentUser={user}
            newActionRef={newActionRef}
          />
        )}
        {tab === 'calendar' && (
          <CalendarTab
            currentUser={user}
            newActionRef={newActionRef}
          />
        )}
      </div>

      {/* Report modal */}
      {showReport && <ReportModal store={store} onClose={() => setShowReport(false)} />}

      {/* Bulletin board */}
      {showBulletins && <BulletinBoard user={user} onClose={() => setShowBulletins(false)} />}

      {/* Bulletin check modal — fires on load/focus */}
      {bulletinCheck && user && (
        <BulletinModal user={user} onDone={() => setBulletinCheck(false)} />
      )}
    </div>
  );
}

const hdrBtn = { background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', color: '#94a3b8', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' };
